/**
 * Team Admin TanStack Query hooks.
 */

import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { userKeys } from "@/queries/keys";
import {
  getTeamMembers,
  getTeamInvites,
  getGroups,
  fetchAllTeamMembers,
} from "./api";
import { getAssignToList } from "@/features/profile/api";
import type { AssignToItem } from "@/features/profile/api";
import type {
  TeamMemberListParams,
  TeamMemberListResponse,
  TeamInvite,
  UserGroup,
} from "./api";

/** Fetch paginated team member list. */
export function useTeamMembers(
  params: TeamMemberListParams = {},
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: userKeys.teamMembers(params as Record<string, unknown>),
    queryFn: async () => {
      const res = await getTeamMembers(params);
      if (!res.success)
        throw new Error(res.error ?? "Failed to fetch team members");
      return res.data as TeamMemberListResponse;
    },
    placeholderData: keepPreviousData,
    enabled: options?.enabled ?? true,
  });
}

/**
 * Fetch all team members (for group/notify assignment dropdowns).
 * Pass a `filterName` to search server-side by name/email — the unfiltered
 * call still loads up to 1000 members so existing selections stay resolvable.
 */
export function useAllTeamMembers(filterName = "") {
  return useQuery({
    queryKey: userKeys.teamMembers({ all: true, filterName }),
    queryFn: async () => {
      const res = await fetchAllTeamMembers(filterName);
      if (!res.success)
        throw new Error(res.error ?? "Failed to fetch all team members");
      return res.data as TeamMemberListResponse;
    },
    placeholderData: keepPreviousData,
  });
}

/** Fetch team invite settings. */
export function useTeamInvites(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: userKeys.teamInvites(),
    queryFn: async () => {
      const res = await getTeamInvites(true);
      if (!res.success)
        throw new Error(res.error ?? "Failed to fetch invite settings");
      return res.data as { invite: TeamInvite | null };
    },
    enabled: options?.enabled ?? true,
  });
}

/** Fetch user groups. */
export function useGroups(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: userKeys.teamGroups(),
    queryFn: async () => {
      const res = await getGroups();
      if (!res.success)
        throw new Error(res.error ?? "Failed to fetch groups");
      return (res.data ?? []) as UserGroup[];
    },
    enabled: options?.enabled ?? true,
  });
}

/** Fetch assign-to list for resolving folder assignTo IDs → display names. */
export function useAssignToList(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: userKeys.assignToList(),
    queryFn: async () => {
      const res = await getAssignToList();
      if (!res.success) throw new Error(res.error ?? "Failed to fetch assign-to list");
      return (res.data ?? []) as AssignToItem[];
    },
    enabled: options?.enabled ?? true,
  });
}
